import { yupResolver } from '@hookform/resolvers/yup';
import { TextField } from '@material-ui/core';
import React, { FormEventHandler, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { AdministrationUserCreateForm, UserExistsByEmail } from 'types';
import { sanitizeAlphaNumericAndSpecial } from 'utils/formatter';
import { findUserByEmail } from 'services/apiOperations';
import { goBack } from 'utils/history';
import { Routes } from 'routes';
import { transformObjectId } from 'services/apiPlatformTransformer';
import { useStyles } from './styles';
import { AdministrationUserCreateStepProps } from './types';
import { AdministrationUserCreateStep2Schema } from './Step2.yup';
import { DialogUserExistsAndNotBelongToAdmin } from './DialogUserExistsAndNotBelongToAdmin';
import { notify } from '../../Notifier';
import { DialogUserExistsAndIsBelongToAdmin } from './DialogUserExistsAndIsBelongToAdmin';

export const Step2: React.FC<AdministrationUserCreateStepProps> = ({
  onSubmit,
  formName,
  previousStepData,
  setFormContext,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('translation', { keyPrefix: 'administration.users.create' });
  const schema = useMemo(() => AdministrationUserCreateStep2Schema(t), [t]);
  const history = useHistory();

  const defaultValues = {
    userId: undefined,
    email: '',
  };

  const { control, setValue, getValues, trigger } = useForm<AdministrationUserCreateForm>({
    mode: 'onBlur',
    defaultValues: { ...defaultValues, ...previousStepData },
    resolver: yupResolver(schema),
  });

  const onStepSubmit: FormEventHandler = async e => {
    e.preventDefault();

    const formIsValid = await schema.isValid(getValues());
    if (!formIsValid) {
      await trigger('email');
      return;
    }

    return findUserByEmail(getValues().email ?? '')
      .then(userFound => {
        if (userFound && userFound.user) {
          setUserFoundByEmail(userFound);
          if (!userFound.isBelongToAdministrator) {
            setOpenDialogNotBelongToAdmin(true);
          } else {
            setOpenDialogIsBelongToAdmin(true);
          }
        } else {
          setFormContext({ userExists: undefined });
          setValue('userId', undefined);
          setValue('firstName', '');
          setValue('lastName', '');

          onSubmit(getValues());
        }
      })
      .catch(err => {
        notify({
          message: t('errorGeneric'),
          severity: 'error',
        });
        return;
      });
  };

  const [openDialogNotBelongToAdmin, setOpenDialogNotBelongToAdmin] = useState(false);
  const [openDialogIsBelongToAdmin, setOpenDialogIsBelongToAdmin] = useState(false);
  const [userFoundByEmail, setUserFoundByEmail] = useState<UserExistsByEmail | null>(null);

  const handleContinueAction = () => {
    setFormContext({ userExists: userFoundByEmail });
    setValue('userId', userFoundByEmail?.user ? transformObjectId(userFoundByEmail.user) : undefined);
    setValue('firstName', userFoundByEmail?.user?.firstName ?? '');
    setValue('lastName', userFoundByEmail?.user?.lastName ?? '');

    onSubmit(getValues());
  };

  const handleShowAction = () => {
    history.push(`/administration/users/${transformObjectId(userFoundByEmail?.user)}`);
  };

  const handleCancelAction = () => {
    goBack(Routes.AdministrationUsers, history);
  };

  return (
    <>
      <DialogUserExistsAndNotBelongToAdmin
        isOpen={openDialogNotBelongToAdmin}
        setOpen={setOpenDialogNotBelongToAdmin}
        userExistByEmail={userFoundByEmail}
        handleCancel={() => {
          handleCancelAction();
        }}
        handleContinue={() => {
          handleContinueAction();
        }}
      />
      <DialogUserExistsAndIsBelongToAdmin
        isOpen={openDialogIsBelongToAdmin}
        setOpen={setOpenDialogIsBelongToAdmin}
        userExistByEmail={userFoundByEmail}
        handleCancel={() => {
          handleCancelAction();
        }}
        handleContinue={() => {
          handleShowAction();
        }}
      />
      <form
        className={classes.form}
        id={formName}
        onSubmit={e => {
          onStepSubmit(e);
        }}
      >
        <div className={classes.fieldLineEmail}>
          <Controller
            name="email"
            control={control}
            render={({ field: { ref, onChange, ...inputProps }, fieldState }) => (
              <TextField
                fullWidth
                id="email"
                variant="outlined"
                label={t('email')}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                inputRef={ref}
                inputProps={{ maxLength: 255 }}
                InputLabelProps={{ required: true }}
                onChange={e => {
                  setValue('email', sanitizeAlphaNumericAndSpecial(e.target.value));
                }}
                {...inputProps}
              />
            )}
          />
        </div>
      </form>
    </>
  );
};
